import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import Checkbox from "@/components/ui/Checkbox.jsx";
import Radio from "@/components/ui/Radio.jsx";
import Sheet from "@/sot/primitives/Sheet.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";

const EXPAND_MORE_ICON = "/icons/appointments/dashboard/expand_more.svg";
const SEARCH_ICON = "/icons/medical-institution/search.svg";
const CLOSE_ICON = "/icons/afrimed/close.svg";

function optionValue(option) {
  return typeof option === "object" ? option.value : option;
}

function optionLabel(option) {
  return typeof option === "object" ? option.label : option;
}

function normalize(value = "") {
  return String(value).trim().replace(/\s+/g, " ").toLocaleLowerCase();
}

/**
 * Shares one searchable shell for patient catalogues while the caller controls
 * single/multi selection and whether literal additions exist. Desktop options
 * are rendered in a viewport-bounded portal; mobile uses the shared sheet.
 */
export default function InstitutionPatientAnchoredSelect({
  id,
  label,
  value,
  values = [],
  options = [],
  placeholder = "Select",
  multiple = false,
  onChange,
  onValuesChange,
  allowCustom = false,
  disabled = false,
  showSelectedChips = true,
  searchPlaceholder,
  selectedValuesLabel,
  loading = false,
  loadingLabel = "Loading options…",
  noOptionsLabel = "No options found.",
  addLabel = "Add",
  moreResultsLabel = "Type to narrow the results.",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mobilePresentation, setMobilePresentation] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  const [menuPosition, setMenuPosition] = useState(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const modalRef = useRef(null);
  const selected = multiple ? values : value ? [value] : [];
  const normalizedSelected = useMemo(() => new Set(selected.map(normalize)), [selected]);
  const normalizedQuery = normalize(query);
  const displayLabel = (selectedValue) => optionLabel(options.find((option) => normalize(optionValue(option)) === normalize(selectedValue))) || selectedValue;
  const display = multiple ? selected.map(displayLabel).join(", ") : displayLabel(value || "");
  const filtered = useMemo(() => {
    if (!open) return [];
    return options.filter((option) => normalize(optionLabel(option)).includes(normalizedQuery));
  }, [open, options, normalizedQuery]);
  const exactMatchExists = useMemo(() => open && (options.some((option) => normalize(optionLabel(option)) === normalizedQuery)
    || selected.some((option) => normalize(option) === normalizedQuery)), [open, options, normalizedQuery, selected]);
  // Medication contains 11k+ rows. Rendering every row turns a simple tap into
  // a multi-second synchronous DOM task. Keep selection fully searchable while
  // rendering a bounded initial result set.
  const visibleOptions = useMemo(() => filtered.slice(0, normalizedQuery ? 240 : 120), [filtered, normalizedQuery]);
  const hasMoreResults = filtered.length > visibleOptions.length;

  function isSelected(option) {
    return normalizedSelected.has(normalize(optionValue(option)));
  }

  function dismiss({ returnFocus = true } = {}) {
    setOpen(false);
    setQuery("");
    if (returnFocus) requestAnimationFrame(() => triggerRef.current?.focus());
  }

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const sync = () => setMobilePresentation(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useLayoutEffect(() => {
    if (!open || mobilePresentation || !triggerRef.current) return undefined;
    const positionMenu = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const gutter = 16;
      const gap = 8;
      const below = viewportHeight - rect.bottom - gutter;
      const above = rect.top - gutter;
      const openUpward = below < 300 && above > below;
      const maxHeight = Math.max(160, Math.min(420, (openUpward ? above : below) - gap));
      const width = Math.min(Math.max(rect.width, 280), viewportWidth - gutter * 2);
      const left = Math.max(gutter, Math.min(rect.left, viewportWidth - gutter - width));
      setMenuPosition({
        left,
        top: openUpward ? Math.max(gutter, rect.top - gap - maxHeight) : rect.bottom + gap,
        width,
        maxHeight,
        upward: openUpward,
      });
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => { window.removeEventListener("resize", positionMenu); window.removeEventListener("scroll", positionMenu, true); };
  }, [open, mobilePresentation]);

  useEffect(() => {
    if (!open) return undefined;
    requestAnimationFrame(() => searchRef.current?.focus());
    function onPointerDown(event) {
      if (!modalRef.current?.contains(event.target) && !triggerRef.current?.contains(event.target)) dismiss();
    }
    function onKeyDown(event) {
      if (event.key === "Escape") { event.preventDefault(); dismiss(); }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  function updateMultiple(option) {
    const optionKey = normalize(option);
    onValuesChange?.(normalizedSelected.has(optionKey)
      ? selected.filter((item) => normalize(item) !== optionKey)
      : [...selected, option]);
  }

  function select(option) {
    if (multiple) {
      updateMultiple(option);
      return;
    }
    onChange?.(option);
    dismiss();
  }

  function addCustom() {
    const next = query.trim().replace(/\s+/g, " ");
    if (!next || exactMatchExists) return;
    if (multiple) onValuesChange?.([...selected, next]);
    else onChange?.(next);
    dismiss();
  }

  function remove(item) {
    onValuesChange?.(selected.filter((entry) => normalize(entry) !== normalize(item)));
  }

  function moveOptionFocus(event) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const optionsArea = modalRef.current?.querySelector('.institution-patient-select__options');
    const optionElements = [...(optionsArea?.querySelectorAll(multiple ? '[role="option"]' : '[role="radio"]') || [])];
    if (!optionElements.length) return;
    event.preventDefault();
    const currentIndex = optionElements.indexOf(document.activeElement);
    const nextIndex = event.key === 'Home'
      ? 0
      : event.key === 'End'
        ? optionElements.length - 1
        : event.key === 'ArrowUp'
          ? Math.max(0, currentIndex - 1)
          : Math.min(optionElements.length - 1, Math.max(0, currentIndex + 1));
    optionElements[nextIndex]?.focus();
  }

  function selectFromKeyboard(event, option) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      select(option);
      return;
    }
    moveOptionFocus(event);
  }

  const menuContents = (
    <div className="institution-patient-select__menu-content">
      {mobilePresentation ? <div className="institution-patient-select__sheet-header"><h2>{label}</h2><button type="button" onClick={() => dismiss()} aria-label={`Close ${label}`}>×</button></div> : null}
      <div className="institution-patient-select__search">
        <img src={SEARCH_ICON} alt="" aria-hidden="true" />
        <input ref={searchRef} value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => {
          if (event.key === "Enter" && allowCustom && normalizedQuery && !exactMatchExists) { event.preventDefault(); addCustom(); }
          else moveOptionFocus(event);
        }} placeholder={searchPlaceholder || `Search ${label.toLocaleLowerCase()}${allowCustom ? " or add" : ""}`} />
        {query ? <button type="button" className="institution-patient-select__clear" onClick={() => setQuery("")} aria-label={`Clear ${label} search`}>×</button> : null}
      </div>
      {showSelectedChips && multiple && selected.length ? <div className="institution-patient-select__menu-chips">{selected.map((item) => <span className="institution-patient-select__chip" key={item}>{displayLabel(item)}<button type="button" aria-label={`Remove ${displayLabel(item)}`} onClick={() => remove(item)}><img src={CLOSE_ICON} alt="" aria-hidden="true" /></button></span>)}</div> : null}
      <div id={`patient-select-${id}-options`} className="institution-patient-select__options" role={multiple ? "listbox" : "radiogroup"} aria-multiselectable={multiple || undefined}>
        {loading ? <p role="status" className="institution-patient-select__status">{loadingLabel}</p> : null}
        {!loading && allowCustom && normalizedQuery && !exactMatchExists ? <div className="institution-patient-select__option institution-patient-select__option--add" role="option" aria-selected="false" tabIndex={0} onClick={addCustom} onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") { event.preventDefault(); addCustom(); }
          else moveOptionFocus(event);
        }}>+ {addLabel} “{query.trim().replace(/\s+/g, " ")}”</div> : null}
        {!loading && visibleOptions.map((option) => {
          const optionId = optionValue(option);
          const optionIsSelected = isSelected(option);
          const optionContent = <><span>{optionLabel(option)}</span>{multiple
            ? <Checkbox checked={optionIsSelected} onChange={() => updateMultiple(optionId)} ariaLabel={`Select ${optionLabel(option)}`} className="institution-patient-select__checkbox" />
            : <Radio checked={optionIsSelected} onChange={() => select(optionId)} name={`Select ${optionLabel(option)}`} variant="afrimed" onKeyDown={moveOptionFocus} />}</>;
          return multiple
            ? <div key={optionId} role="option" aria-selected={optionIsSelected} tabIndex={0} className="institution-patient-select__option" onClick={() => select(optionId)} onKeyDown={(event) => selectFromKeyboard(event, optionId)}>{optionContent}</div>
            : <div key={optionId} className="institution-patient-select__option" onClick={() => select(optionId)}>{optionContent}</div>;
        })}
        {!loading && hasMoreResults ? <p className="institution-patient-select__status">{moreResultsLabel}</p> : null}
        {!loading && !filtered.length && !(allowCustom && normalizedQuery && !exactMatchExists) ? <p className="institution-patient-select__status">{noOptionsLabel}</p> : null}
      </div>
    </div>
  );

  const selectSurface = open && typeof document !== "undefined"
    ? mobilePresentation
      ? <Sheet open onClose={() => dismiss()} ariaLabel={`${label} options`}><div ref={modalRef} className="institution-patient-select__mobile-sheet-content">{menuContents}</div></Sheet>
      : createPortal(<div ref={modalRef} className={`institution-patient-select__menu institution-patient-select__menu--floating${menuPosition?.upward ? " is-upward" : ""}`} role="dialog" aria-label={`${label} search`} style={menuPosition || undefined}>{menuContents}</div>, document.body)
    : null;

  return (
    <div className="institution-patient-select" data-testid={`patient-select-${id}`}>
      <TextField56
        id={id}
        label={label}
        value={display}
        placeholder={placeholder}
        as="button"
        disabled={disabled}
        tone="surface"
        inputRef={triggerRef}
        onTriggerClick={() => setOpen((current) => !current)}
        triggerAriaLabel={`Open ${label}`}
        triggerAriaHaspopup={multiple ? "listbox" : "radiogroup"}
        triggerAriaExpanded={open}
        triggerTrailingInteractive
        triggerProps={{
          "aria-controls": `patient-select-${id}-options`,
          onKeyDown: (event) => {
            if (event.key === "ArrowDown") { event.preventDefault(); setOpen(true); }
          },
        }}
        trailingSlot={<img src={EXPAND_MORE_ICON} alt="" aria-hidden="true" className={open ? "rotate-180" : ""} />}
      />
      {showSelectedChips && multiple && selected.length ? <div className="institution-patient-select__chips" aria-label={selectedValuesLabel || `${label} selected values`}>
        {selected.map((item) => <span className="institution-patient-select__chip" key={item}>{displayLabel(item)}<button type="button" aria-label={`Remove ${displayLabel(item)}`} onClick={() => remove(item)}><img src={CLOSE_ICON} alt="" aria-hidden="true" /></button></span>)}
      </div> : null}
      {selectSurface}
    </div>
  );
}
